import axios from 'axios';

export const WS_BASE_URL =
  // API_URL is for SSR only, won't exist at runtime, where we use URL/location
  `${(process.env.API_URL ?? globalThis.location.origin).replace('http', 'ws')}/ws`;

/**
 * Only use this in SSR where we have to forward the cookies from the request.
 * Prefer using `api` directly at runtime.
 *
 * @param {{cookie?: string}} headers The headers to forward, will be filtered down to only the headers we need
 */
export const createApi = (headers = {}) =>
  axios.create({
    // API_URL is for SSR only, won't exist at runtime, so will default with undefined
    baseURL: process.env.API_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      // Setting an empty cookie would still trigger 'Refused to set unsafe header "cookie"'
      ...(headers.cookie ? { cookie: headers.cookie } : {}),
    },
  });
export const api = createApi();

export const statusDict = {
  // 4XX Client Errors
  400: 'Bad Request',
  401: 'Unauthorized',
  402: 'Payment Required',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  406: 'Not Acceptable',
  407: 'Proxy Authentication Required',
  408: 'Request Timeout',
  409: 'Conflict',
  410: 'Gone',
  411: 'Length Required',
  412: 'Precondition Failed',
  413: 'Payload Too Large',
  414: 'URI Too Long',
  415: 'Unsupported Media Type',
  416: 'Range Not Satisfiable',
  417: 'Expectation Failed',
  418: "I'm a teapot", // RFC 2324
  421: 'Misdirected Request',
  422: 'Unprocessable Entity',
  423: 'Locked',
  424: 'Failed Dependency',
  426: 'Upgrade Required',
  428: 'Precondition Required',
  429: 'Too Many Requests',
  431: 'Request Header Fields Too Large',
  451: 'Unavailable For Legal Reasons',

  // 5XX Server Errors
  500: 'Internal Server Error',
  501: 'Not Implemented',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
  505: 'HTTP Version Not Supported',
  506: 'Variant Also Negotiates',
  507: 'Insufficient Storage',
  508: 'Loop Detected',
  510: 'Not Extended',
  511: 'Network Authentication Required',
};

export async function disconnectSocialAccount(accountId) {
  try {
    await api.delete(`/api/v2/auth/social-accounts/${accountId}/disconnect/`);
    return { severity: 'success', message: 'Account disconnected' };
  } catch (error) {
    return { severity: 'error', message: 'Failed to disconnect account', error: error.message };
  }
}

export async function updateUserProfile(userData) {
  try {
    const response = await api.put('/api/v2/auth/user/profile/', [userData, userData]);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to update profile', error: error.message };
  }
}

export async function eventRunSubmission(formData) {
  try {
    const response = await api.post('/api/v2/submissions/create/', formData);

    return {
      data: response.data,
      severity: 'success',
      message: 'Successfully submit run submission',
    };
  } catch (error) {
    // ok here message can either be a string or a an object contaning an array of descriptions
    // I am not using getErrorMEssage because it required to change too much of the existing structure
    let errorMessage = 'Failed to submit run';
    if (error.response.data.message.description) {
      const firstDescription = error.response.data.message.description[0];
      errorMessage = firstDescription;
    } else if (typeof error.response.data.message === 'string') {
      errorMessage = error.response.data.message;
    }

    return {
      data: null,
      severity: 'error',
      message: errorMessage,
      error: error.response.message,
    };
  }
}

export async function eventJoinRace(formData) {
  try {
    const response = await api.post('/api/v2/submissions/join/', formData);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to join race', error: error.message };
  }
}

export async function getOpenEvents() {
  try {
    const response = await api.get('/api/v2/events/open/');

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch open events', error: error.message };
  }
}

export async function getActiveEvents() {
  try {
    const response = await api.get('/api/v2/events/active/');

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch open events', error: error.message };
  }
}

export async function getActiveOpenEvents() {
  try {
    const response = await api.get('/api/v2/events/active/');
    const response2 = await api.get('/api/v2/events/open/');

    return { active: response.data, open: response2.data };
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch open events', error: error.message };
  }
}

export const generateRaceCode = async () => {
  const response = await api.get('/api/v2/submissions/generate-race-code/');

  return response.data.raceCode;
};

export async function getSubmission(id) {
  try {
    const response = await api.get(`/api/v2/submissions/${id}/`);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch submission', error: error.message };
  }
}

export async function updateSubmission(id, formData) {
  const response = await api.patch(`/api/v2/submissions/${id}/edit/`, formData);

  return response.data;
}

export async function getSubmissionByRaceCode(raceCode) {
  try {
    const response = await api.get(`/api/v2/submission/race/${raceCode}/`);

    return response.data;
  } catch (error) {
    return {
      status: 'error',
      message: 'Failed to fetch submission by race code',
      error: error.response?.data?.detail || error.message,
    };
  }
}

export async function getEventTickets(eventId) {
  try {
    const response = await api.get(`/api/v2/events/${eventId}/tickets/`);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch tickets', error: error.message };
  }
}

export async function updateOrderPickedUpStatus(id, type, pickedUp) {
  try {
    const response = await api.post(`/api/v2/orders/${type}/${id}/picked_up/${pickedUp}/`);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to update picked up status', error: error.message };
  }
}

export async function getOrderedProducts(id) {
  try {
    const response = await api.get(`/api/v2/orders/products/${id}`);

    return response.data;
  } catch (error) {
    return { status: 'error', message: 'Failed to fetch ordered products', error: error.message };
  }
}

const getErrorMessage = error => {
  if (!error.response || !error.response.data || !error.config || !error.config.data) {
    return 'Unknown error occurred.';
  }

  const { statusText, status, data } = error.response;
  const configData =
    error.config.data instanceof FormData
      ? Object.fromEntries(error.config.data.entries())
      : JSON.parse(error.config.data);
  const key = Object.keys(data)[0]; // Get the first key in the response data
  const value = configData[key] ?? 'N/A'; // Get the value from the request data

  return `${statusText}: ${status} - Key: ${key} Value: ${value} Error: ${data[key][0]}`;
};

const getErrorJSON = error => {
  if (!error.response || typeof error.response.data !== 'object') {
    // fallback to a non‑field error
    return { nonFieldErrors: 'Unknown error occurred.' };
  }

  const { data } = error.response;

  return Object.keys(data).reduce((errs, field) => {
    const messages = data[field];
    // use the first message if it's an array, otherwise whatever it is
    errs[field] = Array.isArray(messages) ? messages[0] : messages;
    return errs;
  }, {});
};

const checkStatus = status => {
  const isError = status >= 400 && status < 600;
  let message = statusDict[status];
  if (status >= 400 && status < 500) {
    message = isError ? `Client Error ${statusDict[status]}` : 'Unknown Client Error';
  } else if (status >= 500 && status <= 511) {
    message = isError ? `Server Error ${statusDict[status]}` : 'Unknown Server Error';
  }

  return [isError, message];
};

const successObject = (data, type, modelType) =>
  /** @type {const} */ ({
    data,
    reason: null,
    message: `Successfully ${type} ${modelType} data.`,
    severity: 'success',
  });

const errorObject = (message, modelType, id) =>
  /** @type {const} */ ({
    data: null,
    reason: message,
    message: `${modelType} ID: ${id} not found. Status: ${message}`,
    severity: 'warning',
  });

const trycatchObject = (isError, message, status, type, modelType, id) =>
  /** @type {const} */ ({
    data: null,
    reason: `isError: ${isError}. Reason: ${message}`,
    message: `Status: ${status}. Failed to ${type} ${modelType} ID: ${id}.`, // Error: ${errorMessage} for field '${field}'
    severity: message.includes('Client') ? 'warning' : 'error',
  });

export const handleGetAllv2 = async (url, modelType) => {
  try {
    const response = await api.get(`/api/v2/${url}/`);

    return {
      data: response.data,
      reason: null,
      message: `Successfully fetched ${modelType} data.`,
      severity: 'success',
    };
  } catch (error) {
    return {
      data: null,
      reason: error,
      message: `Failed to fetched ${modelType} data. ${getErrorMessage(error)}`,
      severity: 'error',
    };
  }
};

export const handleSetPatchById = async (url, id, formData, modelType) => {
  try {
    const response = await api.patch(`/api/v2/${url}/${id}/`, formData);
    const [isError, message] = checkStatus(response.status);
    if (isError) return errorObject(message, modelType, id);

    return successObject(response.data, 'patched', modelType);
  } catch (error) {
    const [isError, message] = checkStatus(error.response?.status || 666);

    return trycatchObject(
      isError,
      message,
      error.response?.status || 666,
      'patched',
      modelType,
      id,
    );
  }
};

export const handlePost = async (url, modelType) => {
  try {
    const response = await api.post(url);
    const [isError, message] = checkStatus(response.status);
    if (isError) return errorObject(message, modelType, 0);

    return successObject(response.data, 'exported', modelType);
  } catch (error) {
    const [isError, message] = checkStatus(error.response?.status || 666);

    return trycatchObject(
      isError,
      message,
      error.response?.status || 666,
      'exported',
      modelType,
      0,
    );
  }
};

export const handleEventPost = async (url, data, modelType) => {
  try {
    const response = await api.post(url, data);
    const [isError, message] = checkStatus(response.status);
    if (isError) return errorObject(message, modelType, 0);

    return successObject(response.data, 'exported', modelType);
  } catch (error) {
    const [isError, message] = checkStatus(error.response?.status || 666);

    return trycatchObject(
      isError,
      message,
      error.response?.status || 666,
      'exported',
      modelType,
      0,
    );
  }
};

export const handleCreate = async (url, formData, modelType) => {
  try {
    const response = await api.post(`/api/v2/${url}/`, formData);

    return {
      data: response.data,
      reason: null,
      message: `Successfully created ${modelType} data.`,
      severity: 'success',
    };
  } catch (error) {
    return {
      data: null,
      reason: error,
      errors: getErrorJSON(error),
      message: `Error failed to create "${modelType}" data. ${getErrorMessage(error)}`,
      severity: 'error',
    };
  }
};

const trycatchEmailObject = (isError, message, status, type) => ({
  data: null,
  reason: `isError: ${isError}.
    Reason: ${message}`,
  message: `Status: ${status}. Failed to ${type}. ${message}`,
  severity: message === '' ? 'error' : 'warning',
});

export const handleEmailServicePost = async (url, dict = {}) => {
  try {
    // Assuming the emailPayload contains all necessary data to send the email
    const response = await api.post(`/api/v2/${url}/`, dict);

    // Successful response: for email services, it may only confirm if the action was successful
    return successObject(response.data, 'emailed', 'notification');
  } catch (error) {
    const [isError, message] = checkStatus(error.response?.status || 666);

    return trycatchEmailObject(
      isError,
      message,
      error.response?.status || 666,
      'send email notifaction',
    );
  }
};
